(function () {
  const STORAGE_KEY = "announcement_closed";
  const STORAGE_KEY_TEXT = "announcement_text";
  const MOBILE_BREAKPOINT = 765;
  const SECOND = 1000;
  const DAY = 24 * 60 * 60 * SECOND;
  const HOUR = 60 * 60 * SECOND;
  const MINUTE = 60 * SECOND;

  /* ------------------------------------------------------------------ */
  /* Bar height + close state                                           */
  /* ------------------------------------------------------------------ */

  // Set to true once every message has expired, so the bar collapses instead
  // of leaving an empty strip above the header.
  let allMessagesExpired = false;

  const getText = () =>
    window.ANNOUNCEMENT_TEXT ||
    document.querySelector(".announcement-bar__slides")?.textContent?.trim() ||
    "";

  const getHeight = () => {
    const heightDesktop = window.ANNOUNCEMENT_HEIGHT_DESKTOP || 0;
    const heightMobile = window.ANNOUNCEMENT_HEIGHT_MOBILE || 0;

    return window.innerWidth >= MOBILE_BREAKPOINT
      ? `${heightDesktop}px`
      : `${heightMobile}px`;
  };

  const isClosed = () => {
    const savedText = localStorage.getItem(STORAGE_KEY_TEXT)?.trim() || "";

    return (
      localStorage.getItem(STORAGE_KEY) === "true" && savedText === getText()
    );
  };

  const apply = () => {
    const root = document.documentElement;
    const closed = allMessagesExpired || isClosed();

    root.classList.toggle("announcement-hidden", closed);
    root.style.setProperty(
      "--announcement-height",
      closed ? "0px" : getHeight()
    );
  };

  const closeAnnouncement = () => {
    localStorage.setItem(STORAGE_KEY, "true");
    localStorage.setItem(STORAGE_KEY_TEXT, getText());
    apply();
  };

  const bindClose = () => {
    document
      .querySelectorAll(".announcement-bar__close-button")
      .forEach((btn) => {
        btn.onclick = closeAnnouncement;
      });
  };

  /* ------------------------------------------------------------------ */
  /* Countdown helpers                                                  */
  /* ------------------------------------------------------------------ */

  // The section renders the deadline with the shop's own UTC offset for that
  // date (`+0200`), which Date only reliably parses with a colon. The naive
  // `YYYY-MM-DDThh:mm` fallback is used when the shop value failed to render.
  const parseDeadline = (shopDate, fallbackDate) => {
    const candidates = [];

    if (shopDate) candidates.push(shopDate.replace(/([+-]\d{2})(\d{2})$/, "$1:$2"));
    if (fallbackDate) candidates.push(fallbackDate);

    for (const candidate of candidates) {
      const date = new Date(candidate);
      if (!Number.isNaN(date.getTime())) return date;
    }

    return null;
  };

  // Kept in step with the CSS rather than hard-coded, so the parking timeout
  // always matches the real travel time.
  const readDuration = (element) => {
    const raw = getComputedStyle(element)
      .getPropertyValue("--duration-default")
      .trim();
    const value = parseFloat(raw);

    if (!value) return 300;
    return raw.endsWith("ms") ? value : value * 1000;
  };

  // No leading zeros — matches the design ("2d : 3h : 34m : 44s").
  const setValue = (element, value) => {
    if (element) element.textContent = String(value);
  };

  /* ------------------------------------------------------------------ */
  /* Carousel                                                           */
  /* ------------------------------------------------------------------ */

  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      if (this.isReady) return;
      this.isReady = true;

      this.slides = Array.from(
        this.querySelectorAll(".announcement-bar__slide")
      );
      this.prevButton = this.querySelector(".announcement-bar__nav--prev");
      this.nextButton = this.querySelector(".announcement-bar__nav--next");
      this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

      this.transitionDuration = readDuration(this);
      this.autoplayEnabled = this.dataset.autoplay === "true";
      this.autoplaySpeed =
        Math.max(3, parseFloat(this.dataset.autoplaySpeed) || 5) * SECOND;

      this.index = 0;
      this.autoplayTimer = null;
      this.countdownTimer = null;
      this.pausedByPointer = false;
      this.pausedByEditor = false;

      this.countdowns = this.slides
        .map((slide) => this.readCountdown(slide))
        .filter(Boolean);

      this.pause = this.pause.bind(this);
      this.resume = this.resume.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);

      // A tap on a touch screen fires a synthesized `mouseenter` with no
      // matching `mouseleave`, which would pause autoplay for good.
      if (window.matchMedia("(hover: hover)").matches) {
        this.addEventListener("mouseenter", this.pause);
        this.addEventListener("mouseleave", this.resume);
      }
      this.addEventListener("focusin", this.pause);
      this.addEventListener("focusout", this.resume);
      document.addEventListener("visibilitychange", this.onVisibilityChange);

      this.prevButton?.addEventListener("click", () => {
        this.select(this.index - 1, "backward");
        this.startAutoplay();
      });
      this.nextButton?.addEventListener("click", () => {
        this.select(this.index + 1);
        this.startAutoplay();
      });

      if (this.countdowns.length) {
        this.renderCountdowns();
        this.countdownTimer = setInterval(
          () => this.renderCountdowns(),
          SECOND
        );
      }

      this.select(0);

      // Flush layout so the first message is settled in place before
      // transitions are switched on, otherwise it travels in on page load.
      void this.offsetHeight;
      this.classList.add("announcement-bar--ready");

      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      clearInterval(this.countdownTimer);
      document.removeEventListener("visibilitychange", this.onVisibilityChange);
    }

    visibleSlides() {
      return this.slides.filter((slide) => !slide.hidden);
    }

    select(target, direction = "forward") {
      const slides = this.visibleSlides();
      const isSingle = slides.length < 2;

      this.classList.toggle("announcement-bar--single", isSingle);
      [this.prevButton, this.nextButton].forEach((button) => {
        if (!button) return;
        button.tabIndex = isSingle ? -1 : 0;
        button.setAttribute("aria-hidden", isSingle ? "true" : "false");
      });

      if (isSingle) this.stopAutoplay();

      if (!slides.length) {
        allMessagesExpired = true;
        apply();
        return;
      }

      this.index = ((target % slides.length) + slides.length) % slides.length;
      const active = slides[this.index];
      const leaving = this.slides.find((slide) =>
        slide.classList.contains("is-active")
      );

      if (active === leaving) return;

      this.classList.toggle(
        "announcement-bar--backward",
        direction === "backward"
      );

      // Clicking faster than the travel can pick a slide that is still on its
      // way out. Dropping it back to the parked state here means the flush
      // below moves it to the entry side too, so it never doubles back.
      clearTimeout(active.parkTimer);
      active.classList.remove("is-leaving");

      // Waiting slides carry no transition, so this flush silently moves them
      // to the side they will now enter from. Without it the incoming slide is
      // still parked on the old side and "previous" would slide in from the
      // right.
      void this.offsetHeight;

      active.classList.add("is-active");
      active.setAttribute("aria-hidden", "false");

      if (!leaving) return;

      leaving.classList.remove("is-active");
      leaving.classList.add("is-leaving");
      leaving.setAttribute("aria-hidden", "true");

      // Each departing slide finishes its own exit, so clicking faster than the
      // animation stacks them up instead of yanking the previous one off
      // mid-travel.
      clearTimeout(leaving.parkTimer);
      leaving.parkTimer = setTimeout(() => {
        leaving.classList.remove("is-leaving");
      }, this.transitionDuration);
    }

    startAutoplay() {
      this.stopAutoplay();

      if (!this.autoplayEnabled) return;
      if (this.pausedByPointer || this.pausedByEditor || document.hidden) return;
      if (this.reducedMotion.matches) return;
      if (this.visibleSlides().length < 2) return;

      this.autoplayTimer = setInterval(
        () => this.select(this.index + 1),
        this.autoplaySpeed
      );
    }

    stopAutoplay() {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    pause() {
      this.pausedByPointer = true;
      this.stopAutoplay();
    }

    resume() {
      this.pausedByPointer = false;
      this.startAutoplay();
    }

    onVisibilityChange() {
      if (document.hidden) this.stopAutoplay();
      else this.startAutoplay();
    }

    readCountdown(slide) {
      const root = slide.querySelector("[data-countdown]");
      if (!root) return null;

      const endsAt = parseDeadline(
        root.dataset.countdown,
        root.dataset.countdownFallback
      );
      if (!endsAt) return null;

      return {
        slide,
        endsAt,
        onEnd: root.dataset.countdownEnd,
        timer: root.querySelector("[data-countdown-timer]"),
        endedText: root.querySelector("[data-countdown-ended]"),
        // The days number and the separator after it, hidden together.
        dayParts: Array.from(
          root.querySelectorAll('[data-countdown-unit="days"]')
        ),
        values: {
          days: root.querySelector('[data-countdown-value="days"]'),
          hours: root.querySelector('[data-countdown-value="hours"]'),
          minutes: root.querySelector('[data-countdown-value="minutes"]'),
          seconds: root.querySelector('[data-countdown-value="seconds"]'),
        },
        ended: false,
      };
    }

    renderCountdowns() {
      const now = Date.now();
      let expired = false;

      this.countdowns.forEach((countdown) => {
        const distance = countdown.endsAt.getTime() - now;

        if (distance <= 0) {
          if (countdown.ended) return;
          countdown.ended = true;
          expired = true;

          if (countdown.onEnd === "show_text" && countdown.endedText) {
            if (countdown.timer) countdown.timer.hidden = true;
            countdown.endedText.hidden = false;
          } else {
            countdown.slide.hidden = true;
            countdown.slide.classList.remove("is-active");
          }
          return;
        }

        const days = Math.floor(distance / DAY);

        countdown.dayParts.forEach((part) => {
          part.hidden = days === 0;
        });

        setValue(countdown.values.days, days);
        setValue(countdown.values.hours, Math.floor((distance % DAY) / HOUR));
        setValue(
          countdown.values.minutes,
          Math.floor((distance % HOUR) / MINUTE)
        );
        setValue(
          countdown.values.seconds,
          Math.floor((distance % MINUTE) / SECOND)
        );
      });

      if (!expired) return;

      if (this.countdowns.every((countdown) => countdown.ended)) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }

      // A slide may have just disappeared from under the active index.
      this.select(this.index);
      this.startAutoplay();
    }
  }

  if (!customElements.get("announcement-bar")) {
    customElements.define("announcement-bar", AnnouncementBar);
  }

  /* ------------------------------------------------------------------ */
  /* Theme editor                                                       */
  /* ------------------------------------------------------------------ */

  const onBlockSelect = (event) => {
    const slide = event.target?.closest?.(".announcement-bar__slide");
    const bar = slide?.closest("announcement-bar");
    if (!bar || !slide) return;

    bar.pausedByEditor = true;
    bar.stopAutoplay();

    const index = bar.visibleSlides().indexOf(slide);
    if (index >= 0) {
      bar.select(index, index < bar.index ? "backward" : "forward");
    }
  };

  const onBlockDeselect = (event) => {
    const bar = event.target?.closest?.("announcement-bar");
    if (!bar) return;

    bar.pausedByEditor = false;
    bar.startAutoplay();
  };

  /* ------------------------------------------------------------------ */
  /* Boot                                                               */
  /* ------------------------------------------------------------------ */

  const init = () => {
    allMessagesExpired = false;
    apply();
    bindClose();
  };

  let resizeRaf = null;
  const onResize = () => {
    if (resizeRaf) cancelAnimationFrame(resizeRaf);
    resizeRaf = requestAnimationFrame(apply);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // The section re-injects this script on every editor reload, so the
  // document-level listeners are only bound once.
  if (!window.__announcementBarBound) {
    window.__announcementBarBound = true;

    window.addEventListener("resize", onResize);
    document.addEventListener("shopify:section:load", init);
    document.addEventListener("shopify:section:change", init);
    document.addEventListener("shopify:section:select", init);
    document.addEventListener("shopify:block:select", onBlockSelect);
    document.addEventListener("shopify:block:deselect", onBlockDeselect);
  }
})();
